// Tenantra Inc. - Authentication Service
import api from './api';
import { identify, resetAnalytics } from './analytics';

const TOKEN_KEY = 'tenantra_access_token';
const REFRESH_TOKEN_KEY = 'tenantra_refresh_token';
const USER_KEY = 'tenantra_user';

/**
 * Store authentication tokens
 */
export function setTokens(accessToken, refreshToken) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Get access token
 */
export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get refresh token
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store user data
 */
export function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  identify(user);
}

/**
 * Get current user
 */
export function getCurrentUser() {
  const userData = localStorage.getItem(USER_KEY);
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  const token = getAccessToken();
  const user = getCurrentUser();
  return !!(token && user);
}

/**
 * Clear authentication data
 */
export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Register new landlord
 */
export async function register(data) {
  const response = await api.post('/auth/register', data);
  const { access_token, refresh_token, user } = response.data;
  
  setTokens(access_token, refresh_token);
  setUser(user);
  
  return user;
}

/**
 * Login user
 * Returns MFA challenge data if MFA is enabled, otherwise returns user
 */
export async function login(email, password, recaptchaToken = null) {
  const payload = { email, password };
  if (recaptchaToken) {
    payload.recaptcha_token = recaptchaToken;
  }
  
  const response = await api.post('/auth/login', payload);
  
  // Check if MFA is required
  if (response.data.mfa_required) {
    // Return MFA challenge data - don't store tokens yet
    return {
      mfa_required: true,
      mfa_token: response.data.mfa_token,
      mfa_method: response.data.mfa_method,
      phone_last_four: response.data.phone_last_four
    };
  }
  
  // No MFA - proceed with normal login
  const { access_token, refresh_token, user } = response.data;
  
  setTokens(access_token, refresh_token);
  setUser(user);
  
  return user;
}

/**
 * Refresh access token
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  
  const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
  const { access_token, refresh_token: newRefreshToken, user } = response.data;
  
  setTokens(access_token, newRefreshToken);
  setUser(user);
  
  return access_token;
}

/**
 * Logout user
 */
export function logout() {
  resetAnalytics();
  clearAuth();
  window.location.href = '/login';
}

/**
 * Get current user from API
 */
export async function fetchCurrentUser() {
  const response = await api.get('/auth/me');
  setUser(response.data);
  return response.data;
}
